<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" :saving="loading" @cancel="closeModal()">
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full">
          <div class="flex flex-col gap-y-5">
            <Switch
              v-model="rawPayoutCreditEntry.debt"
              :error="errors?.debt"
              :options="{ yes: $t('payoutCreditEntry.debt'), no: $t('payoutCreditEntry.credit') }"
              :label="$t('payoutCreditEntry.debtOrCredit')"
            />
            <Multiselect
              v-model="rawPayoutCreditEntry.onlyForCampaignRegistration"
              :error="errors?.onlyForCampaignRegistration"
              :options="campaignRegistrations"
              :label="$t('payoutCreditEntry.onlyForCampaignRegistration')"
              class="w-full"
              :can-deselect="true"
              :can-clear="true"
              :searchable="true"
            />
            <FormInput
              v-if="payoutCreditEntry?.id !== undefined"
              :model-value="payoutCreditEntry?.author?.fullName ?? ''"
              :label="$t('payoutCreditEntry.author')"
              class="w-full"
              :disabled="true"
            />
            <FormInput
              v-model="rawPayoutCreditEntry.amount"
              :error="errors?.amount"
              class="col-start-1 col-end-2"
              type="number"
              :label="$t('payoutCreditEntry.amount')"
              :placeholder="$t('payoutCreditEntry.amount')"
            />
            <div>
              <FormInput
                v-model="rawPayoutCreditEntry.subject"
                :error="errors?.subject"
                class="col-start-1 col-end-2"
                :label="$t('payoutCreditEntry.subject')"
                :placeholder="$t('payoutCreditEntry.subject')"
              >
                <template #label>
                  {{ $t('payoutCreditEntry.subject') }}
                  <span class="font-normal">
                    {{ `(${$t('payoutCreditEntry.driverLanguage')}: ${$t(`language.${driver?.user?.language}`)})` }}
                  </span>
                </template>
              </FormInput>
              <p class="pt-1 text-sm text-red">{{ $t('payoutCreditEntry.visibleForDriver') }}</p>
            </div>
            <text-area
              v-model="rawPayoutCreditEntry.comment"
              :error="errors?.comment"
              :label="$t('payoutCreditEntry.comment')"
              class="w-full"
            />
          </div>
        </div>
        <div class="w-full text-center">
          <custom-button
            variant="primary"
            class="mt-15"
            :busy="loading"
            @click="save"
          >
            {{ $t('save') }}
          </custom-button>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import {computed, onMounted, reactive, ref} from 'vue';
import {useVModel} from '@vueuse/core';
import {useI18n} from 'vue-i18n';
import {useValidation} from '@hooks/form';
import {boolean, number, object, string,} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import FormInput from '@utils/inputs/Input.vue';
import {useNotify} from '@utils/notifications';
import {useFetchAction} from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import {syncRef} from '@hooks/state';
import CustomButton from '@utils/CustomButton.vue';
import TextArea from '@utils/inputs/TextArea.vue';
import Switch from '@utils/inputs/Switch.vue';
import {collOptionsProvider, withExtraOptions} from '@utils/table/hooks/provider';
import Multiselect from '@utils/Multiselect.vue';
import type {CampaignRegistration, Driver, PayoutCreditEntry} from '@/entities/Entities';
import {useStore} from '@/store';
import type {Option} from '@utils/filter/filterTypes/Dropdown.vue';

interface Props {
  open: boolean;
  driver: Driver;
  payoutCreditEntry?: PayoutCreditEntry;
}

const emit = defineEmits(['update:open', 'update:payoutCreditEntry', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalPayoutCreditEntry = useVModel(props, 'payoutCreditEntry', emit);

const isNew = computed(() => !props.payoutCreditEntry);
const tKey = computed(() =>
  (isNew.value
    ? 'pages.driver.payoutCreditEntryList.modal.new'
    : 'pages.driver.payoutCreditEntryList.modal.edit'));

const { t } = useI18n();

const notify = useNotify();

const driver = computed(() => props.driver);

const driverFilter = computed(() => ({ driver: props.driver?.id ?? '' }));
const params = reactive({
  filter: driverFilter,
});

const campaignRegistrations = withExtraOptions(collOptionsProvider<CampaignRegistration>(
  '/campaign_registrations',
  params,
  (value) => ({
    label: value.campaign.group.name,
    value: value['@id'],
  }),
), [
  { label: t('payoutCreditEntry.noCampaignRegistration'), value: 'null' },
]);

const initialData = (payoutCreditEntry?: PayoutCreditEntry) =>
  (payoutCreditEntry
    ? {
      ...payoutCreditEntry,
      onlyForCampaignRegistration: payoutCreditEntry.onlyForCampaignRegistration?.['@id'] ?? 'null',
    }
    : {
      debt: false,
      amount: undefined,
      comment: undefined,
      subject: undefined,
      onlyForCampaignRegistration: undefined,
    });

const rawPayoutCreditEntry = syncRef(internalPayoutCreditEntry, (payoutCreditEntry) => ({
  ...initialData(payoutCreditEntry),
}));

const {
  data: resPayoutCreditEntry,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    amount: number()
      .when('debt', {
        is: true,
        then: number().max(20, t('validation.max20')).typeError(t('validation.number')),
      })
      .nullable()
      .typeError(t('validation.number'))
      .positive(t('validation.positive'))
      .required(t('validation.notEmpty')),
    comment: string().nullable(),
    subject: string().nullable().required(t('validation.notEmpty')),
    debt: boolean().nullable().required(t('validation.notEmpty')),
    onlyForCampaignRegistration: string().nullable().required(t('validation.notEmpty')),
  }),
  rawPayoutCreditEntry,
);

function resetData() {
  rawPayoutCreditEntry.value = { ...initialData() } as unknown as typeof resPayoutCreditEntry['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalPayoutCreditEntry.value = undefined;
}

const [exec, { loading }] = useFetchAction();

const store = useStore();
const currentUser = computed(() => store.state?.auth?.user?.['@id'] ?? null);

const save = validatedFn(async () => {
  try {
    const edit = !!props.payoutCreditEntry;
    const url = edit ? props.payoutCreditEntry['@id'] : '/payout_credit_entries';

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        amount: resPayoutCreditEntry.value.amount,
        author: props.payoutCreditEntry?.id !== undefined ? undefined : currentUser.value,
        debt: resPayoutCreditEntry.value.debt,
        subject: resPayoutCreditEntry.value.subject,
        comment: resPayoutCreditEntry.value.comment,
        driver: driver.value['@id'],
        onlyForCampaignRegistration: resPayoutCreditEntry.value.onlyForCampaignRegistration === 'null' ? null : resPayoutCreditEntry.value.onlyForCampaignRegistration,
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
});
</script>

<style lang="scss" scoped></style>
